import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, ChatMessage as ChatMessageType } from '../../types';
import './Chat.css';

interface ChatProps {
  contact: User;
  currentUser: User;
}

const Chat: React.FC<ChatProps> = ({ contact, currentUser }) => {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageTimestamp = useRef<string | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async (isInitialFetch = false) => {
    let url = `/api/chat/messages/${contact.id}`;
    if (!isInitialFetch && lastMessageTimestamp.current) {
      const timestamp = new Date(lastMessageTimestamp.current).getTime();
      url += `?since=${timestamp}`;
    }

    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          // Update timestamp with the last message from THIS fetch to prevent re-fetching
          const lastReceivedMessage = data.messages[data.messages.length - 1];
          lastMessageTimestamp.current = lastReceivedMessage.created_at;

          // Add only the messages that are actually new
          setMessages(prevMessages => {
              const existingIds = new Set(prevMessages.map(m => m.id));
              const newMessages = data.messages.filter((m: ChatMessageType) => !existingIds.has(m.id));
              
              if (newMessages.length === 0) {
                  return prevMessages; // If no new messages, return old state to prevent re-render
              }
              
              return [...prevMessages, ...newMessages];
          });
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [contact.id]);

  useEffect(() => {
    lastMessageTimestamp.current = null;
    setMessages([]);
    fetchMessages(true);
    const interval = setInterval(() => fetchMessages(false), 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [contact.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const response = await fetch('/api/chat/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: contact.id,
          message: newMessage,
        }),
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(false); // Refresh messages after sending
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <div className="chat-container">
      <h3>Chat with {contact.first_name} {contact.last_name}</h3>
      <div className="messages-list">
        {messages.map((msg) => (
          <div key={msg.id} className={`message ${msg.sender_id === currentUser.id ? 'sent' : 'received'}`}>
            <p>{msg.message}</p>
            <span className="timestamp">{new Date(msg.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSendMessage} className="message-form">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
        />
        <button type="submit">Send</button>
      </form>
    </div>
  );
};

export default Chat; 